// Playlists on the server: one row per playlist of a signed-in account, which
// its owner's devices and, while it is shared, everyone holding its link edit
// (#325; live Sep 2026, migration 0012; every playlist, migration 0014).
//
// What makes it small is that playlist entries are typed REFS and the corpora
// ship with the app: an iReal ref CARRIES its chunk and openbook/realbook refs
// resolve against bundled manifests client-side, so the only thing hosted is
// the playlist document itself. A playlist gets its row when it is made (or
// when its device first signs in), private; sharing sets what a link holder
// may do. From then on every linked device saves through
// `save_shared_playlist`, naming the revision it edited, asks
// `playlist_revisions` which rows changed, and reads the changed ones through
// `open_shared_playlists` — by id only, so the link is the key.
// src/data/sharedPlaylistSync.ts decides when.

import type { PlaylistDoc, PlaylistEntry } from '@/storage/playlists';
import { PLAYLIST_SCHEMA_VERSION, makeEntryId, parsePlaylistTuneRef } from '@/storage/playlists';
import { linkAccessOf, type LinkAccess } from '@/storage/playlistLinks';
import { normalizePlaylistCall } from '@/state/tunePlayback';
import { getSupabaseRaw } from '@/auth/session';

const TABLE = 'shared_setlists';
/** Ids per request. The server refuses a request naming more than 200 (open)
 * or 500 (revisions) and answers NOTHING, which the sync would read as every
 * playlist gone — so an account with many playlists asks in batches. */
const IDS_PER_REQUEST = 100;

function batches<T>(items: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += IDS_PER_REQUEST) {
    out.push(items.slice(i, i + IDS_PER_REQUEST));
  }
  return out;
}
/** A class set is dozens of rows; hundreds is corrupted data, not a setlist. */
const MAX_ENTRIES = 500;

// --- the fetched document is untrusted input -------------------------------

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * Shape-check a fetched setlist document.
 *
 * Another account wrote these bytes, so nothing is taken on faith: a doc with
 * no usable identity is refused whole, a malformed ENTRY is dropped rather
 * than sinking the set (the same posture as `checkSavedTunes` — keep what is
 * sound), and a missing or DUPLICATE entryId is minted so the set cursor and
 * reorder keep working: every row lookup finds the first row with an id, so
 * two rows sharing one leave the second unaddressable (and sent the set walk
 * back to the first forever). Minted ids are derived from the doc and the row's
 * place, so reading the same doc twice gives the same ids — a shared playlist
 * is read every few seconds, and fresh ids each time would look like an edit.
 * Timestamps fall back to epoch, which makes an undated remote doc LOSE the
 * newer-wins comparison against any local copy — the safe direction.
 */
export function parseSharedSetlistDoc(value: unknown): PlaylistDoc | null {
  if (!value || typeof value !== 'object') return null;
  const doc = value as Record<string, unknown>;
  if (doc.kind !== 'playlist') return null;
  const id = str(doc.id);
  const name = str(doc.name);
  if (!id || !name || id.length > 80 || name.length > 200) return null;
  const rawEntries = Array.isArray(doc.entries) ? doc.entries.slice(0, MAX_ENTRIES) : [];
  const entries: PlaylistEntry[] = [];
  const entryIds = new Set<string>();
  rawEntries.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object') return;
    const entry = raw as Record<string, unknown>;
    const ref = parsePlaylistTuneRef(entry.ref);
    if (!ref) return;
    const note = str(entry.note);
    const call = normalizePlaylistCall(entry.call);
    let entryId = str(entry.entryId);
    for (let n = 0; !entryId || entryIds.has(entryId); n++) {
      entryId = makeEntryId(`${id}#${index}#${n}`);
    }
    entryIds.add(entryId);
    entries.push({
      entryId,
      ref,
      ...(note ? { note } : {}),
      ...(call ? { call } : {}),
    });
  });
  const epoch = new Date(0).toISOString();
  const description = str(doc.description);
  return {
    kind: 'playlist',
    id,
    schemaVersion: PLAYLIST_SCHEMA_VERSION,
    name,
    ...(description ? { description } : {}),
    entries,
    createdAt: str(doc.createdAt) ?? epoch,
    updatedAt: str(doc.updatedAt) ?? epoch,
  };
}

export function setlistShareUrl(shareId: string, origin?: string): string {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/setlist/${shareId}`;
}

// --- the Supabase client, structurally ---------------------------------------

type SingleResult = Promise<{
  data: Record<string, unknown> | null;
  error: { message: string } | null;
}>;
type ListResult = {
  data: Record<string, unknown>[] | null;
  error: { message: string } | null;
};

/** An eq-filtered select: awaitable as a LIST (supabase builders are
 * thenables), or narrowed further / taken as a single row. */
type SetlistFilter = PromiseLike<ListResult> & {
  eq(column: string, value: string): SetlistFilter;
  maybeSingle(): SingleResult;
};

/** An eq-filtered delete, narrowed as far as it needs. */
type DeleteFilter = PromiseLike<{ error: { message: string } | null }> & {
  eq(column: string, value: string): DeleteFilter;
};

/** The slice of supabase-js this module uses (tests fake it). */
export interface SetlistsClient {
  auth: {
    getSession(): Promise<{ data: { session: { user: { id: string } } | null } }>;
  };
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): PromiseLike<{ data: unknown; error: { message: string } | null }>;
  from(table: string): {
    select(columns: string): {
      eq(column: string, value: string): SetlistFilter;
    };
    upsert(
      row: Record<string, unknown> | Record<string, unknown>[],
      options: { onConflict: string; ignoreDuplicates?: boolean },
    ): PromiseLike<{ error: { message: string } | null }> & {
      select(columns: string): {
        single(): SingleResult;
        maybeSingle(): SingleResult;
      };
    };
    update(row: Record<string, unknown>): {
      eq(column: string, value: string): PromiseLike<{ error: { message: string } | null }>;
    };
    delete(): {
      eq(column: string, value: string): DeleteFilter;
    };
  };
}

let testClient: SetlistsClient | null = null;

function client(): SetlistsClient | null {
  return testClient ?? (getSupabaseRaw() as SetlistsClient | null);
}

/**
 * Share a playlist: insert its row, editable by anyone holding the link. An
 * UPSERT on (owner, playlist_id), so sharing a playlist that already has a row
 * keeps its link — the playlist page adopts an existing row before it ever
 * offers to share, so this only overwrites one in a race.
 */
export async function publishSetlist(
  list: PlaylistDoc,
): Promise<{ shareId: string; url: string; revision: number; access: LinkAccess }> {
  const c = client();
  if (!c) throw new Error('Sign in to share playlists.');
  const { data, error } = await c
    .from(TABLE)
    .upsert(
      {
        playlist_id: list.id,
        name: list.name,
        doc: list as unknown as Record<string, unknown>,
        link_access: 'edit',
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'owner,playlist_id' },
    )
    .select('share_id, revision, link_access')
    .single();
  const shareId = typeof data?.share_id === 'string' ? data.share_id : null;
  if (error || !shareId) throw new Error(error?.message ?? 'Sharing failed. Try again.');
  return {
    shareId,
    url: setlistShareUrl(shareId),
    revision: typeof data?.revision === 'number' ? data.revision : 1,
    access: linkAccessOf(data?.link_access),
  };
}

/**
 * Give a playlist its row in your account, private. Answers null when the
 * account already holds this playlist (made on another device, or by another
 * tab a moment ago): the existing row is never overwritten here, the caller
 * opens it instead and the sync merges the two.
 */
export async function createAccountPlaylist(
  list: PlaylistDoc,
): Promise<{ shareId: string; revision: number } | null> {
  const c = client();
  if (!c) throw new Error('Sign in to keep playlists.');
  const { data, error } = await c
    .from(TABLE)
    .upsert(
      {
        playlist_id: list.id,
        name: list.name,
        doc: list as unknown as Record<string, unknown>,
        link_access: 'none',
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'owner,playlist_id', ignoreDuplicates: true },
    )
    .select('share_id, revision')
    .maybeSingle();
  if (error) throw new Error(error.message);
  const shareId = typeof data?.share_id === 'string' ? data.share_id : null;
  if (!shareId) return null;
  return { shareId, revision: typeof data?.revision === 'number' ? data.revision : 1 };
}

/** One shared playlist as a link holder reads it. `doc` is null when the
 * stored document is unreadable. */
export interface SharedPlaylistRow {
  shareId: string;
  doc: PlaylistDoc | null;
  access: LinkAccess;
  revision: number;
  /** This account shared it. */
  mine: boolean;
  updatedAt: string | null;
}

/**
 * Open the shared playlists these links name, by id. A link missing from the
 * answer does not resolve for this account: never shared, unshared since, or
 * the reader has no access. Transport failures throw, so a caller can tell
 * "couldn't reach" from "this link is dead".
 */
export async function openSharedPlaylists(
  shareIds: readonly string[],
): Promise<Map<string, SharedPlaylistRow>> {
  const out = new Map<string, SharedPlaylistRow>();
  const c = client();
  if (!c || shareIds.length === 0) return out;
  const answers: unknown[] = [];
  for (const batch of batches(shareIds)) {
    const { data, error } = await c.rpc('open_shared_playlists', { share_ids: batch });
    if (error) throw new Error(error.message);
    if (Array.isArray(data)) answers.push(...data);
  }
  for (const raw of answers) {
    const row = raw as Record<string, unknown>;
    if (typeof row.share_id !== 'string') continue;
    out.set(row.share_id, {
      shareId: row.share_id,
      doc: parseSharedSetlistDoc(row.doc),
      access: linkAccessOf(row.link_access),
      revision: typeof row.revision === 'number' ? row.revision : 1,
      mine: row.mine === true,
      updatedAt: typeof row.updated_at === 'string' ? row.updated_at : null,
    });
  }
  return out;
}

/** Where one playlist stands on the server, without its document. */
export interface PlaylistRevision {
  access: LinkAccess;
  revision: number;
  mine: boolean;
}

/**
 * Which of these playlists changed: the revisions alone, under the same rule
 * as opening them. A link missing from the answer does not resolve for this
 * account (deleted, unshared, or no access); transport failures throw.
 */
export async function playlistRevisions(
  shareIds: readonly string[],
): Promise<Map<string, PlaylistRevision>> {
  const out = new Map<string, PlaylistRevision>();
  const c = client();
  if (!c || shareIds.length === 0) return out;
  const answers: unknown[] = [];
  for (const batch of batches(shareIds)) {
    const { data, error } = await c.rpc('playlist_revisions', { share_ids: batch });
    if (error) throw new Error(error.message);
    if (Array.isArray(data)) answers.push(...data);
  }
  for (const raw of answers) {
    const row = raw as Record<string, unknown>;
    if (typeof row.share_id !== 'string') continue;
    out.set(row.share_id, {
      access: linkAccessOf(row.link_access),
      revision: typeof row.revision === 'number' ? row.revision : 1,
      mine: row.mine === true,
    });
  }
  return out;
}

/** What a save came to: saved at a new revision; a conflict (someone saved
 * first — merge and try again); view only; or gone (unshared). */
export type SaveOutcome =
  | { status: 'saved'; revision: number }
  | { status: 'conflict'; revision: number }
  | { status: 'view-only' }
  | { status: 'gone' };

/** Save a new version of a shared playlist, made against `expectedRevision`. */
export async function saveSharedPlaylist(
  shareId: string,
  doc: PlaylistDoc,
  expectedRevision: number,
): Promise<SaveOutcome> {
  const c = client();
  if (!c) throw new Error('Sign in to edit shared playlists.');
  const { data, error } = await c.rpc('save_shared_playlist', {
    target: shareId,
    new_doc: doc,
    expected_revision: expectedRevision,
  });
  if (error) throw new Error(error.message);
  const result = (data ?? {}) as Record<string, unknown>;
  const revision = typeof result.revision === 'number' ? result.revision : expectedRevision;
  switch (result.status) {
    case 'saved':
      return { status: 'saved', revision };
    case 'conflict':
      return { status: 'conflict', revision };
    case 'view-only':
      return { status: 'view-only' };
    case 'gone':
      return { status: 'gone' };
    default:
      throw new Error('The save was not understood.');
  }
}

/** Choose what link holders may do. Only the owner's change lands (RLS). */
export async function setSharedPlaylistAccess(shareId: string, access: LinkAccess): Promise<void> {
  const c = client();
  if (!c) throw new Error('Sign in first.');
  const { error } = await c.from(TABLE).update({ link_access: access }).eq('share_id', shareId);
  if (error) throw new Error(error.message);
}

/** One of this account's published setlists, as the management view reads it.
 * `playlistId` names the LOCAL playlist it was published from — a share can
 * outlive its local copy (deleted here, still live for the class), which is
 * exactly why the list exists. */
export interface OwnShare {
  shareId: string;
  playlistId: string;
  name: string;
  /** 'none' is a private playlist: in the account, not shared. */
  access: LinkAccess;
  updatedAt: string | null;
}

/** Everything this account has published, newest name first. Signed out (or
 * before the client boots) answers empty rather than throwing — the section
 * that renders this simply has nothing to say yet. */
export async function listOwnShares(): Promise<OwnShare[]> {
  const c = client();
  if (!c) return [];
  const { data: sessionData } = await c.auth.getSession();
  const uid = sessionData.session?.user.id;
  if (!uid) return [];
  const { data, error } = await c
    .from(TABLE)
    .select('share_id, playlist_id, name, link_access, updated_at')
    .eq('owner', uid);
  if (error) throw new Error(error.message);
  const shares = (data ?? []).map((r) => ({
    shareId: String(r.share_id),
    playlistId: typeof r.playlist_id === 'string' ? r.playlist_id : '',
    name: typeof r.name === 'string' ? r.name : '',
    access: linkAccessOf(r.link_access),
    updatedAt: typeof r.updated_at === 'string' ? r.updated_at : null,
  }));
  shares.sort((a, b) => a.name.localeCompare(b.name));
  return shares;
}

// --- the shared playlists this account opened (migration 0013) ------------------

const FOLLOWS = 'shared_playlist_follows';

async function signedInUser(c: SetlistsClient): Promise<string | null> {
  const { data } = await c.auth.getSession();
  return data.session?.user.id ?? null;
}

/**
 * Record that this account opened these shared playlists, so every device it
 * signs in on can find them. Idempotent: a playlist already recorded keeps its
 * first date.
 */
export async function followSharedPlaylists(shareIds: readonly string[]): Promise<void> {
  if (shareIds.length === 0) return;
  const c = client();
  if (!c) return;
  const uid = await signedInUser(c);
  if (!uid) return;
  const { error } = await c.from(FOLLOWS).upsert(
    shareIds.map((share_id) => ({ user_id: uid, share_id })),
    { onConflict: 'user_id,share_id', ignoreDuplicates: true },
  );
  if (error) throw new Error(error.message);
}

/** A shared playlist this account opened, and when it first did. */
export interface FollowedShare {
  shareId: string;
  followedAt: string | null;
}

/** The shared playlists this account opened, on any device. */
export async function listFollowedShares(): Promise<FollowedShare[]> {
  const c = client();
  if (!c) return [];
  const uid = await signedInUser(c);
  if (!uid) return [];
  const { data, error } = await c.from(FOLLOWS).select('share_id, followed_at').eq('user_id', uid);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({
    shareId: String(r.share_id),
    followedAt: typeof r.followed_at === 'string' ? r.followed_at : null,
  }));
}

/** Forget a shared playlist this account opened (it was deleted here). */
export async function unfollowSharedPlaylist(shareId: string): Promise<void> {
  const c = client();
  if (!c) return;
  const { error } = await c.from(FOLLOWS).delete().eq('share_id', shareId);
  if (error) throw new Error(error.message);
}

/** Delete one of your playlists from your account: gone from every device
 * of yours, and for everyone holding its link, who keep it as it last stood.
 * Filtered by owner as well, so an admin's delete can never reach a row that
 * is not theirs. */
export async function deleteOwnPlaylist(shareId: string): Promise<void> {
  const c = client();
  if (!c) throw new Error('Sign in first.');
  const uid = await signedInUser(c);
  if (!uid) throw new Error('Sign in first.');
  const { error } = await c.from(TABLE).delete().eq('share_id', shareId).eq('owner', uid);
  if (error) throw new Error(error.message);
}

/** The share id of this account's published copy of a playlist, if any. */
export async function findOwnShare(playlistId: string): Promise<string | null> {
  const c = client();
  if (!c) return null;
  const { data: sessionData } = await c.auth.getSession();
  const uid = sessionData.session?.user.id;
  if (!uid) return null;
  const { data, error } = await c
    .from(TABLE)
    .select('share_id')
    .eq('playlist_id', playlistId)
    .eq('owner', uid)
    .maybeSingle();
  if (error || !data) return null;
  return typeof data.share_id === 'string' ? data.share_id : null;
}

// ---------------------------------------------------------------------------
// Test seams

export function __setSharedSetlistsTestHooks(hooks: { client?: SetlistsClient | null }): void {
  if ('client' in hooks) testClient = hooks.client ?? null;
}
